import { createAction, props } from '@ngrx/store';

import { ProfileSearchEnum } from '../../enums/profile-search';
import {
  WalkingFarmLeadStatusEnum,
  WalkingFarmMainTabEnum,
  WalkingFarmNewTabEnum,
  WalkingFarmPropertiesOwnerStatusFilterEnum
} from '../../enums/walking-farm';
import {
  OwnerOccupiedFilter,
  PolygonFarmCoordinates,
  PropertyTypeFilter,
  RadiusFarmCoordinates,
  WalkingFarmAlertsResponse,
  WalkingFarmLoadSavedFarmsResponse,
  WalkingFarmProperty,
  WalkingFarmPropertyAlertsResponse,
  WalkingFarmUnreadAlert
} from '../../typings/walking-farm';
import { StoredWalkingFarm } from '../models/walking-farm.model';

export const toggleDrawMode = createAction('[Walking Farm] Toggle Draw Mode');

export const addDrawFarmPoint = createAction(
  '[Walking Farm] Add Draw Farm Point',
  props<{
    payload: { point: google.maps.LatLngLiteral };
  }>()
);

export const addDrawFarmFinalPoint = createAction(
  '[Walking Farm] Add Draw Farm Final Point',
  props<{
    payload: { point: google.maps.LatLngLiteral };
  }>()
);

export const undoDrawFarmPoint = createAction('[Walking Farm] Undo Draw Farm Point');

export const clearDrawFarm = createAction('[Walking Farm] Clear Draw Farm');

export const autoFillDrawFarm = createAction('[Walking Farm] Auto Fill Draw Farm');

export const loadPolygonFarmSummary = createAction(
  '[Walking Farm] Load Polygon Farm Summary',
  props<{
    payload: {
      searchType: ProfileSearchEnum;
    };
  }>()
);

export const loadRadiusFarmSummary = createAction(
  '[Walking Farm] Load Radius Farm Summary',
  props<{
    payload: {
      searchType: ProfileSearchEnum;
    };
  }>()
);

export const loadFarmSummary = createAction(
  '[Walking Farm] Load Farm Summary',
  props<{ payload: { searchType: ProfileSearchEnum; shapeJson: PolygonFarmCoordinates } }>()
);

export const loadFarmSummarySuccess = createAction(
  '[Walking Farm] Load Farm Summary Success',
  props<{
    payload: {
      messages: string[];
      searchType: ProfileSearchEnum;
      total: number;
      turnoverRate: number | null;
    };
  }>()
);

export const loadFarmSummaryFailure = createAction(
  '[Walking Farm] Load Farm Summary Failure',
  props<{ payload: { error: Error } }>()
);

export const refreshKey = createAction('[Walking Farm] Refresh Key');

export const refreshKeySuccess = createAction(
  '[Walking Farm] Refresh Key Success',
  props<{ payload: { key: string | null } }>()
);

export const refreshKeyFailure = createAction(
  '[Walking Farm] Refresh Key Failure',
  props<{ payload: { error: Error } }>()
);

export const resetKey = createAction('[Walking Farm] Reset Key');

export const loadFarmDetails = createAction(
  '[Walking Farm] Load Farm Details',
  props<{ payload: { name: string; searchType: ProfileSearchEnum } }>()
);

export const loadFarmDetailsFailure = createAction(
  '[Walking Farm] Load Farm  Details Failure',
  props<{ payload: { error: Error } }>()
);

export const saveFarmSuccess = createAction(
  '[Walking Farm] Save Farm Success',
  props<{ payload: { id: string; searchType: ProfileSearchEnum } }>()
);

export const saveFarmFailure = createAction('[Walking Farm] Save Farm Failure', props<{ payload: { error: Error } }>());

export const loadSavedFarmsList = createAction('[Walking Farm] Load Saved Farms List');

export const loadSavedFarmsListSuccess = createAction(
  '[Walking Farm] Load Saved Farms List Success',
  props<{ payload: WalkingFarmLoadSavedFarmsResponse[] }>()
);

export const loadSavedFarmsListFailure = createAction(
  '[Walking Farm] Load Saved Farms List Failure',
  props<{ payload: { error: Error } }>()
);

export const updateSavedFarmsListFilter = createAction(
  '[Walking Farm] Update Saved Farms List Filter',
  props<{ payload: { text: string } }>()
);

export const updateNewFarmFilters = createAction(
  '[Walking Farm] Update New Farm Filters',
  props<{
    payload: {
      filterByMaxBathrooms: number | null;
      filterByMaxBedrooms: number | null;
      filterByMaxLivingArea: number | null;
      filterByMinBathrooms: number | null;
      filterByMinBedrooms: number | null;
      filterByMinLivingArea: number | null;
      filterByNoSalesWithinYears: number;
      filterByOwnerOccupied: OwnerOccupiedFilter;
      filterByPropertyType: PropertyTypeFilter[];
    };
  }>()
);

export const clearNewFarmFilters = createAction('[Walking Farm] Clear New Farm Filters');

export const deleteSavedFarm = createAction('[Walking Farm] Delete Saved Farm', props<{ payload: { id: string } }>());

export const deleteSavedFarmFailure = createAction(
  '[Walking Farm] Delete Saved Farm Failure',
  props<{ payload: { error: Error } }>()
);

export const loadFarm = createAction('[Walking Farm] Load Farm', props<{ payload: { id: string } }>());

export const loadFarmFailure = createAction('[Walking Farm] Load Farm Failure', props<{ payload: { error: Error } }>());

export const setWalkingFarm = createAction(
  '[Walking Farm/API] Set Walking Farm',
  props<{ payload: StoredWalkingFarm }>()
);

export const setMainSelectedTab = createAction(
  '[Walking Farm] Set Main Selected Tab',
  props<{ payload: { selectedTab: WalkingFarmMainTabEnum } }>()
);

export const loadAlerts = createAction('[Walking Farm] Load Alerts', props<{ payload: { id: string } }>());

export const loadAlertsSuccess = createAction(
  '[Walking Farm] Load Alerts Success',
  props<{
    payload: WalkingFarmAlertsResponse;
  }>()
);

export const loadAlertsFailure = createAction(
  '[Walking Farm] Load Alerts Failure',
  props<{ payload: { error: Error } }>()
);

export const loadPropertyAlerts = createAction(
  '[Walking Farm] Load Property Alerts',
  props<{
    payload: {
      id: string;
      propertyId: string;
    };
  }>()
);

export const loadPropertyAlertsSuccess = createAction(
  '[Walking Farm] Load Property Alerts Success',
  props<{
    payload: WalkingFarmPropertyAlertsResponse;
  }>()
);

export const loadPropertyAlertsFailure = createAction(
  '[Walking Farm] Load Property Alerts Failure',
  props<{ payload: { error: Error } }>()
);

export const markAlertsAsRead = createAction(
  '[Walking Farm] Mark Alerts As Read',
  props<{
    payload: {
      farmId: string;
      unreadAlerts: WalkingFarmUnreadAlert[];
    };
  }>()
);

export const markAlertsAsReadSuccess = createAction('[Walking Farm] Mark Alerts As Read Success');

export const markAlertsAsReadFailure = createAction(
  '[Walking Farm] Mark Alerts As Read Failure',
  props<{ payload: { error: Error } }>()
);

export const toggleAlertsEnabled = createAction(
  '[Walking Farm] Toggle Alerts Enabled',
  props<{
    payload: {
      enabled: boolean;
      id: string;
    };
  }>()
);

export const toggleAlertsEnabledSuccess = createAction('[Walking Farm] Toggle Alerts Enabled Success');

export const toggleAlertsEnabledFailure = createAction(
  '[Walking Farm] Toggle Alerts Enabled Failure',
  props<{ payload: { error: Error } }>()
);

export const toggleBoundariesVisible = createAction('[Walking Farm] Toggle Boundaries Visible');

export const updateProperty = createAction(
  '[Walking Farm] Update Property',
  props<{
    payload: {
      farmId: string;
      property: WalkingFarmProperty;
    };
  }>()
);

export const updatePropertySuccess = createAction(
  '[Walking Farm] Update Property Success',
  props<{
    payload: {
      farmId: string;
      property: WalkingFarmProperty;
    };
  }>()
);

export const updatePropertyFailure = createAction(
  '[Walking Farm] Update Property Failure',
  props<{ payload: { error: Error } }>()
);

export const updateViewFarmFilters = createAction(
  '[Walking Farm] Update View Farm Filters',
  props<{
    payload: {
      farmId: string;
      filterByLeadStatus: WalkingFarmLeadStatusEnum[];
      filterByOwnerStatus: WalkingFarmPropertiesOwnerStatusFilterEnum;
    };
  }>()
);

export const clearViewFarmFilters = createAction(
  '[Walking Farm] Clear View Farm Filters',
  props<{
    payload: {
      farmId: string;
    };
  }>()
);

export const exportLabels = createAction(
  '[Walking Farm] Export Labels',
  props<{
    payload: {
      farmId: string;
      name: string;
    };
  }>()
);

export const exportPDF = createAction(
  '[Walking Farm] Export PDF',
  props<{
    payload: {
      farmId: string;
      name: string;
    };
  }>()
);

export const exportCSV = createAction(
  '[Walking Farm] Export CSV',
  props<{
    payload: {
      farmId: string;
      name: string;
    };
  }>()
);

export const newConfiguredFarm = createAction(
  '[Walking Farm] New Configured Farm',
  props<{
    payload: {
      searchType: ProfileSearchEnum;
      shapeCoordinates: PolygonFarmCoordinates | RadiusFarmCoordinates;
    };
  }>()
);

export const updateName = createAction(
  '[Walking Farm] Update Name',
  props<{
    payload: {
      farmId: string;
      name: string;
    };
  }>()
);

export const updateNameFailure = createAction(
  '[Walking Farm] Update Name Failure',
  props<{ payload: { error: Error } }>()
);

export const updatePropertiesOrder = createAction(
  '[Walking Farm] Update Properties Order',
  props<{
    payload: {
      farmId: string;
      order: string[];
    };
  }>()
);

export const updatePropertiesOrderSuccess = createAction(
  '[Walking Farm] Update Properties Order Success',
  props<{
    payload: {
      farmId: string;
      order: string[];
    };
  }>()
);

export const updatePropertiesOrderFailure = createAction(
  '[Walking Farm] Update Properties Order Error',
  props<{ payload: { error: Error } }>()
);

export const setDrawModeType = createAction(
  '[Walking Farm] Set Draw Mode Type',
  props<{
    payload: {
      type: WalkingFarmNewTabEnum;
    };
  }>()
);

export const setDrawFarmRadius = createAction(
  '[Walking Farm] Set Draw Farm Radius',
  props<{
    payload: {
      radius: number;
    };
  }>()
);

export const setDrawFarmCenter = createAction(
  '[Walking Farm] Set Draw Farm Center',
  props<{
    payload: {
      center: google.maps.LatLngLiteral;
    };
  }>()
);

export const updatePhoneAndEmailSelectedPropertyIds = createAction(
  '[Walking Farm] Update Phone And Email Selected Property Ids',
  props<{
    payload: {
      farmId: string;
      phoneAndEmailSelectedPropertyIds: string[];
    };
  }>()
);

export const updateAddressSearchLocation = createAction(
  '[Walking Farm] Update Address Search Location',
  props<{ payload: { address: string; location: google.maps.LatLngLiteral } }>()
);

export const geocodeAddressLocation = createAction(
  '[Walking Farm] Geocode Address Location',
  props<{ payload: { address: string } }>()
);

export const geocodeLocationSuccess = createAction(
  '[Walking Farm] Geocode Location Success',
  props<{ payload: { result: google.maps.GeocoderResult } }>()
);

export const geocodeLocationFailure = createAction(
  '[Walking Farm] Geocode Location Failure',
  props<{ payload: { error: Error } }>()
);

export const resetSearch = createAction('[Walking Farm] Reset Search');
